import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileCheck2, AlertTriangle, RefreshCw } from 'lucide-react';
import documentsApi, { type EnvelopeRow } from '../../services/documentsApi';
import { hardwareApi, shortDate, type DeviceOrderRow } from '../../services/hardwareApi';
import { InventoryPage } from './InventoryChrome';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * AGREEMENTS — mockup screen 6 (minimal). `/inventory/agreements`.
 *
 * W5 review I9: the mockup's Inventory nav lists six sections including
 * Agreements ("auto-generated, merge-filled, e-signed at onboarding"), but
 * nothing in super-admin showed a device order's generated agreement or its
 * envelope state — the pre-existing `Legal › Agreements` is the DOCUMENT-
 * GOVERNANCE template list (drafting, versions, publishing), not this.
 *
 * NO NEW BACKEND: this reads envelopes through the exact same
 * `documentsApi.listEnvelopes` / `getEnvelope` admin API the Legal ›
 * Agreements › {template} › Envelopes tab already uses
 * (`src/pages/legal/DocumentDetail.tsx`), and joins them client-side to
 * their device order via `hardwareApi.listOrders()` (`order.envelopeId` —
 * stamped by billing-service at order creation). An envelope with no
 * matching order is a signed agreement from some OTHER flow (not a device
 * finance checkout) and is left out — this page is specifically the
 * per-order device-agreement view screen 6 draws.
 *
 * Same page-of-results honesty `EnvelopesTab` already carries: `listEnvelopes`
 * returns ONE DynamoDB page across every template, filtered in memory — the
 * count on this page is "in the newest page", never a census.
 */

const STATUS_CHIP: Record<string, string> = {
  executed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  sent: 'bg-amber-100 text-amber-700 border-amber-200',
  partially_signed: 'bg-amber-100 text-amber-700 border-amber-200',
  draft: 'bg-stone-100 text-stone-500 border-stone-200',
  declined: 'bg-red-100 text-red-700 border-red-200',
  voided: 'bg-red-100 text-red-700 border-red-200',
  expired: 'bg-red-100 text-red-700 border-red-200',
};
const STATUS_LABEL: Record<string, string> = {
  executed: '✍ Signed',
  sent: 'Sent, unsigned',
  partially_signed: 'Partially signed',
  draft: 'Draft',
  declined: 'Declined',
  voided: 'Voided',
  expired: 'Expired',
};

const AgreementsPage: React.FC = () => {
  const navigate = useNavigate();
  const [envelopes, setEnvelopes] = useState<EnvelopeRow[]>([]);
  const [orderByEnvelopeId, setOrderByEnvelopeId] = useState<Record<string, DeviceOrderRow>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      const [envRes, orderRes] = await Promise.all([
        documentsApi.listEnvelopes({}),
        hardwareApi.listOrders(),
      ]);
      const byEnvelope: Record<string, DeviceOrderRow> = {};
      for (const o of orderRes.items || []) {
        if (o.envelopeId) byEnvelope[o.envelopeId] = o;
      }
      setOrderByEnvelopeId(byEnvelope);
      // Only envelopes a device order actually points at — everything else
      // is a signed document from a different flow, out of scope here.
      setEnvelopes((envRes.envelopes || []).filter((e) => byEnvelope[e.envelopeId]));
    } catch (e: any) {
      setError(e?.message || 'Failed to load device agreements');
    }
  };

  useEffect(() => { (async () => { setLoading(true); await load(); setLoading(false); })(); }, []);
  const refresh = async () => { setRefreshing(true); await load(); setRefreshing(false); };

  if (loading) {
    return (
      <InventoryPage icon={FileCheck2} title="Agreements" subtitle="Device-finance signature envelopes, per order">
        <RubyLoader variant="page" />
      </InventoryPage>
    );
  }

  return (
    <InventoryPage
      icon={FileCheck2}
      title="Agreements"
      subtitle="Device-finance signature envelopes, per order"
      actions={(
        <button
          onClick={refresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 text-gray-300 hover:text-white hover:bg-white/10 text-sm font-medium min-h-[44px] disabled:opacity-50"
        >
          {refreshing ? <RubyGemMark size={15} drawing color="currentColor" /> : <RefreshCw size={15} />} Refresh
        </button>
      )}
    >
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr className="text-left text-[10px] uppercase tracking-wide text-stone-500">
                <th className="px-4 py-3 font-semibold">Envelope</th>
                <th className="px-4 py-3 font-semibold">Order</th>
                <th className="px-4 py-3 font-semibold">Template / version</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Sent</th>
                <th className="px-4 py-3 font-semibold">Executed</th>
                <th className="px-4 py-3 font-semibold">Expires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {envelopes.map((e) => {
                const order = orderByEnvelopeId[e.envelopeId];
                return (
                  <tr key={e.envelopeId} className="hover:bg-stone-50">
                    <td className="px-4 py-3 font-mono text-[11px] text-stone-500">{e.envelopeId}</td>
                    <td className="px-4 py-3">
                      {order ? (
                        <button
                          onClick={() => navigate(`/inventory/orders`)}
                          className="font-mono text-[11px] text-red-500 hover:underline"
                          title={`Open ${order.orderId}`}
                        >
                          {order.orderId}
                        </button>
                      ) : <span className="text-stone-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-stone-700 text-xs">{e.templateDocumentId} — v{e.templateVersionNo}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border ${STATUS_CHIP[e.status] || 'bg-stone-100 text-stone-500 border-stone-200'}`}>
                        {STATUS_LABEL[e.status] || e.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-500 text-xs">{e.sentAt ? shortDate(new Date(e.sentAt).getTime()) : '—'}</td>
                    <td className="px-4 py-3 text-stone-500 text-xs">{e.executedAt ? shortDate(new Date(e.executedAt).getTime()) : '—'}</td>
                    <td className="px-4 py-3 text-stone-500 text-xs">{e.expiresAt ? shortDate(new Date(e.expiresAt).getTime()) : '—'}</td>
                  </tr>
                );
              })}
              {envelopes.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-stone-400">No device-finance agreements yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-3 text-[11px] text-stone-500">
        One page of results, newest first — not a census. An order can&apos;t pass <strong>Packing</strong> while
        its agreement here is not <strong>Signed</strong>.
      </p>
    </InventoryPage>
  );
};

export default AgreementsPage;
